import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { MapPin, Briefcase, Award, Loader2 } from "lucide-react";
import { useAuth, getAuthHeaders } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { CandidateAvatar } from "../components/CandidateAvatar";
import { TimelineItem } from "./ProfessionalProfilePage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTION_STYLE = { background: "white", border: "1px solid #E5E7EB", borderRadius: "20px", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" };
const LABEL_STYLE = { fontSize: "11px", fontWeight: 700, color: "#028CF8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "8px", fontFamily: "Poppins, sans-serif" };

// Authenticated-only preview of "how a company would see this candidate" — NOT a
// public/guest-accessible page (self-guards on `user`, and the data endpoint always
// requires a Bearer token). Lives INSIDE the normal candidate mobile app shell
// (Bottom Nav stays visible, "Perfil" stays marked active — see BottomNav.jsx /
// App.js) instead of a standalone web-style page. Purely presentational: no edit
// controls, no completion %, no sensitive contact/salary data.
export default function CompanyPreviewPage() {
  const { user, loading: authLoading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate("/auth"); return; }
    const load = async () => {
      try {
        const res = await axios.get(`${API}/my/complete-profile`, { headers: getAuthHeaders() });
        setProfile(res.data);
      } catch { navigate("/dashboard?tab=myprofile"); }
      finally { setLoading(false); }
    };
    load();
  }, [user, authLoading, navigate]);

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" data-testid="company-preview-loading">
        <Loader2 className="h-8 w-8 text-[#028CF8] animate-spin" />
      </div>
    );
  }
  if (!profile) return null;

  const prefs = profile.preferences || {};
  const relevantPrefChips = [];
  (prefs.work_mode || []).forEach((v) => relevantPrefChips.push(t(`jobsPage.workMode${v.charAt(0).toUpperCase()}${v.slice(1)}`)));
  (prefs.job_types || []).forEach((v) => relevantPrefChips.push(t(`jobCard.type${v === "full-time" ? "FullTime" : v === "part-time" ? "PartTime" : v.charAt(0).toUpperCase() + v.slice(1)}`)));
  if (prefs.availability) relevantPrefChips.push(t(`profTab.availability${prefs.availability.replace(/_([a-z])/g, (m, c) => c.toUpperCase()).replace(/^([a-z0-9])/, (m, c) => c.toUpperCase())}`));
  if (prefs.willing_to_relocate) relevantPrefChips.push(t("profTab.willingToRelocateLabel"));
  if (prefs.willing_to_travel) relevantPrefChips.push(t("profTab.willingToTravelLabel"));

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="company-preview-page">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-6 max-w-4xl">
        {/* Header — a discreet "Editar perfil" action, NOT a web-style back bar:
            the app shell (Bottom Nav, Perfil marked active) stays in control of navigation. */}
        <div style={{ ...SECTION_STYLE, marginBottom: "16px" }} data-testid="company-preview-header">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <CandidateAvatar name={profile.name} picture={profile.picture} size={72} rounded="xl" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap justify-between">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="font-poppins text-xl font-bold text-[#0F172A]">{profile.name}</h1>
                  {profile.open_to_work && (
                    <span style={{ fontSize: "11px", fontWeight: 700, padding: "3px 10px", borderRadius: "999px", background: "rgba(16,185,129,0.1)", color: "#059669", fontFamily: "Poppins, sans-serif" }}>
                      {t("profTab.availableStatus")}
                    </span>
                  )}
                </div>
                <button type="button" onClick={() => navigate("/dashboard?tab=myprofile")} data-testid="company-preview-edit-profile-link"
                  className="text-xs font-semibold text-[#028CF8] hover:underline font-poppins flex-shrink-0">
                  {t("profTab.backToEditProfile")}
                </button>
              </div>
              {profile.title && <p className="text-sm font-semibold text-[#028CF8] font-poppins mt-0.5">{profile.title}</p>}
              <div className="flex flex-wrap gap-3 mt-1.5">
                {profile.location && (
                  <span className="flex items-center gap-1 text-xs text-[#64748B] font-poppins"><MapPin className="h-3 w-3" /> {profile.location}</span>
                )}
                {profile.years_experience > 0 && (
                  <span className="flex items-center gap-1 text-xs text-[#64748B] font-poppins"><Briefcase className="h-3 w-3" /> {profile.years_experience}+ {t("profile.yearsExp")}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {profile.summary && (
            <section style={SECTION_STYLE} data-testid="company-preview-summary">
              <p style={LABEL_STYLE}>{t("profProfile.summary")}</p>
              <p className="text-sm text-[#374151] font-poppins leading-relaxed">{profile.summary}</p>
            </section>
          )}

          {profile.experience?.filter((e) => e.company?.trim() || e.position?.trim()).length > 0 && (
            <section style={SECTION_STYLE} data-testid="company-preview-experience">
              <p style={LABEL_STYLE}>{t("profProfile.workExp")}</p>
              {profile.experience.filter((e) => e.company?.trim() || e.position?.trim()).map((exp, i) => (
                <TimelineItem key={i} title={exp.position} subtitle={exp.company}
                  dates={`${exp.start_date || ""} — ${exp.current ? t("profProfile.present") : (exp.end_date || "")}`}
                  description={[exp.location, exp.description].filter(Boolean).join(" · ")} />
              ))}
            </section>
          )}

          {profile.education?.filter((e) => e.institution?.trim() || e.degree?.trim()).length > 0 && (
            <section style={SECTION_STYLE} data-testid="company-preview-education">
              <p style={LABEL_STYLE}>{t("profProfile.education")}</p>
              {profile.education.filter((e) => e.institution?.trim() || e.degree?.trim()).map((edu, i) => (
                <TimelineItem key={i} title={edu.degree} subtitle={edu.institution} dates={edu.year}
                  description={[edu.field, edu.location].filter(Boolean).join(" · ")} />
              ))}
            </section>
          )}

          {profile.skills?.length > 0 && (
            <section style={SECTION_STYLE} data-testid="company-preview-skills">
              <p style={LABEL_STYLE}>{t("profProfile.skills")}</p>
              <div className="flex flex-wrap gap-2">
                {profile.skills.map((s) => <span key={s} style={{ fontSize: "12px", fontWeight: 600, padding: "4px 11px", borderRadius: "999px", background: "#EFF6FF", color: "#028CF8", fontFamily: "Poppins, sans-serif" }}>{s}</span>)}
              </div>
            </section>
          )}

          {profile.languages?.length > 0 && (
            <section style={SECTION_STYLE} data-testid="company-preview-languages">
              <p style={LABEL_STYLE}>{t("profProfile.languages")}</p>
              <div className="flex flex-wrap gap-2">
                {profile.languages.map((l) => <span key={l} style={{ fontSize: "12px", fontWeight: 600, padding: "4px 11px", borderRadius: "999px", background: "#F0FDF4", color: "#15803D", fontFamily: "Poppins, sans-serif" }}>{l}</span>)}
              </div>
            </section>
          )}

          {profile.certifications?.length > 0 && (
            <section style={SECTION_STYLE} data-testid="company-preview-certifications">
              <p style={LABEL_STYLE}>{t("profProfile.certifications")}</p>
              <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                {profile.certifications.map((c, i) => (
                  <li key={c.name + i} className="flex items-center gap-2 text-sm text-[#374151] font-poppins" style={{ marginBottom: "8px" }}>
                    <Award className="h-3.5 w-3.5 text-[#64748B] flex-shrink-0" />
                    {c.name}{c.issuer ? ` · ${c.issuer}` : ""}{!c.no_expiry && c.issue_date ? ` · ${c.issue_date}` : ""}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {profile.industries?.length > 0 && (
            <section style={SECTION_STYLE} data-testid="company-preview-industries">
              <p style={LABEL_STYLE}>{t("profTab.industriesTitle")}</p>
              <div className="flex flex-wrap gap-2">
                {profile.industries.map((s) => <span key={s} style={{ fontSize: "12px", fontWeight: 600, padding: "4px 11px", borderRadius: "999px", background: "#F8FAFC", border: "1px solid #E5E7EB", color: "#374151", fontFamily: "Poppins, sans-serif" }}>{s}</span>)}
              </div>
            </section>
          )}

          {relevantPrefChips.length > 0 && (
            <section style={SECTION_STYLE} data-testid="company-preview-preferences">
              <p style={LABEL_STYLE}>{t("profTab.preferencesTitle")}</p>
              <div className="flex flex-wrap gap-2">
                {relevantPrefChips.map((c, i) => <span key={i} style={{ fontSize: "12px", fontWeight: 600, padding: "4px 11px", borderRadius: "999px", background: "#F8FAFC", border: "1px solid #E5E7EB", color: "#374151", fontFamily: "Poppins, sans-serif" }}>{c}</span>)}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
